import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import Reveal from '../Component/Reveal';

const offerings = [
  {
    title: 'Software as a Service',
    short: 'SaaS',
    text: 'A cloud-based procurement and operations suite for managing requisitions, approvals, RFQs, purchase orders, inventory, suppliers, and spend control — configurable to your plants, teams, and business processes',
    to: '/applications',
    className: 'bento-saas',
  },
  {
    title: 'Procurement as a Service',
    short: 'PaaS',
    text: 'Domain-led procurement support for direct and indirect spend, combining sourcing expertise, supplier management, negotiation, and process execution to improve savings, speed, compliance, and purchasing outcomes.',
    to: '/procurement-services',
    className: 'bento-paas',
  },
  {
    title: 'Inventory as a Service',
    short: 'ImaS',
    text: 'Connected inventory solutions that provide shared stock visibility, material tracking, reorder insights, and warehouse alignment across manufacturing locations — helping reduce excess inventory and improve availability.',
    to: '/inventory-management',
    className: 'bento-imas',
  },
  {
    title: 'Procurement consulting',
    short: 'Advisory',
    text: 'Practical consulting for category strategy, sourcing transformation, procurement process improvement, operating-model design, supplier performance, and digital adoption — helping teams build efficient processes and achieve lasting business impact.',
    to: '/procurement-consulting',
    className: 'bento-consult',
  },
];

const steps = [
  { n: '01', title: 'Diagnose', text: 'Map spend, process gaps, and the operating model your teams actually need.' },
  { n: '02', title: 'Design', text: 'Configure workflows, controls, and supplier engagement around your categories.' },
  { n: '03', title: 'Deploy', text: 'Roll out the suite and desks with role-based access, training, and KPIs.' },
  { n: '04', title: 'Operate', text: 'Run, monitor, and continuously improve procurement and inventory outcomes.' },
];

const reasons = [
  { title: 'Advanced SCM practice', text: 'Methods built for measurable operational outcomes, not slideware.' },
  { title: 'Bespoke by design', text: 'Workflows shaped around your categories, plants, and buying teams.' },
  { title: 'Resilient supply', text: 'Supplier agility and continuity designed into every cycle.' },
  { title: 'Sustainable process', text: 'Approvals, audit trails, and spend control without extra complexity.' },
  { title: 'Technology + domain', text: 'IT and procurement expertise in the same delivery motion.' },
  { title: 'Service excellence', text: 'A model that stays accountable for savings, speed, and stakeholder experience.' },
];

const customers = [
  { file: 'Fujiteck.png', name: 'Fujiteck' },
  { file: 'INDNIPPON.NS_BIG-a2bc9ae7.png', name: 'INDNIPPON' },
  { file: 'Itpl.png', name: 'ITPL' },
  { file: 'magna-logo.png', name: 'magna', large: true },
  { file: 'Titan.png', name: 'Titan' },
  { file: 'images.png', name: 'Apollo Tyres', large: true  },
  { file: 'Asianpaints.png', name: 'Asian Paints', large: true },
  { file: 'padgetpng.png', name: 'Padget' },
  { file: 'Bs&b.png', name: 'BS&B' },
  { file: 'Agratas.png', name: 'Agratas' },
  { file: 'Ramco.png', name: 'Ramco' },
  { file: 'ANDERSON-GREENWOOD-CROSBY-LOGO-600X600-AVRIN.png', name: 'Anderson Greenwood Crosby' },
  { file: 'Chemplast_Sanmar.png', name: 'Chemplast Sanmar' },
  { file: 'Flowserve.png', name: 'Flowserve', wide: true },
  { file: 'Sanmar matrix limited.png', name: 'Sanmar Matrix' },
  { file: 'Genlite.png', name: 'Genlite' },
  { file: 'Hatsun.png', name: 'Hatsun' },
  { file: 'Lucas.png', name: 'Lucas' }, 
  { file: 'saint-gobain.png', name: 'Saint-Gobain' },
  { file: 'tata-electronics-original.png', name: 'Tata Electronics' },
  { file: 'jk maini.png', name: 'JK Maini' },
  { file: 'Tvasta.png', name: 'Tvasta' },
  { file: 'cumi-logo.png', name: 'Cumi', large: true },
  { file: 'Dalmia.png', name: 'Dalmia' },
  { file: 'headerlogo.png', name: 'headerlogo' },
  { file: 'indoama-logo.png', name: 'indoama' },
  { file: 'Sierra.png', name: 'Sierra' },
];

function customerSrc(file) {
  return `/Assets/Images/Company-logo/${encodeURIComponent(file).replace(/%2F/g, '/')}`;
}

const members = [
  {
    name: 'Dhanaraj Panneerselvam',
    role: 'Managing Director',
    src: '/Assets/Images/Employees/Employee4.png',
    overview:
      'Leads InstaTenders across platform, practice, and client delivery. Sets the operating model for procurement, inventory, and supplier work so plants get one accountable desk instead of scattered buying.',
    focus: ['Leadership', 'Operating model', 'Client outcomes'],
  },
  {
    name: 'Sivanathan',
    role: 'Procurement Head',
    src: '/Assets/Images/Employees/Employee6.png',
    overview:
      'Runs category strategy and day-to-day sourcing — RFQs, negotiations, and supplier performance. Keeps spend on process so savings, cycle time, and compliance move together.',
    focus: ['Sourcing', 'Categories', 'Supplier desks'],
  },
  {
    name: 'Manikandan',
    role: 'Warehouse Manager',
    src: '/Assets/Images/Employees/employee1.png',
    overview:
      'Owns stores discipline and stock visibility across locations. Aligns receiving, put-away, and reorder signals so inventory stays available without building excess.',
    focus: ['Warehousing', 'Stock visibility', 'Replenishment'],
  },
  {
    name: 'Muthu Selvi',
    role: 'Accounts',
    src: '/Assets/Images/Employees/Employee5.png',
    overview:
      'Looks after commercial control — invoices, three-way match, and spend trails. Makes sure every PO and payment can be audited without slowing the plant.',
    focus: ['Payables', 'Spend control', 'Audit trail'],
  },
  {
    name: 'Naresh Kannan',
    role: 'Partner',
    src: '/Assets/Images/Employees/Employee3.png',
    overview:
      'Partners with leadership on growth, key accounts, and how the practice sits beside the product. Stays close to clients so delivery stays honest after the first go-live.',
    focus: ['Partnerships', 'Key accounts', 'Growth'],
  },
  {
    name: 'Gokul',
    role: 'Operations',
    src: '/Assets/Images/Employees/Employee2.png',
    overview:
      'Keeps engagements on rhythm — staffing the desk, tracking SLAs, and closing the loop between software, buyers, and warehouse teams once the process is live.',
    focus: ['Delivery', 'SLAs', 'Process run'],
  },
];

function TeamProfile({ member, onClose }) {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div className="team-modal-root" role="presentation">
      <button type="button" className="team-modal-backdrop" aria-label="Close profile" onClick={onClose} />
      <div
        className="team-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-modal-name"
      >
        <button type="button" className="team-modal-close" onClick={onClose} aria-label="Close">
          <span aria-hidden="true">×</span>
        </button>
        <figure className="team-modal-media">
          <img src={member.src} alt="" width="480" height="560" />
        </figure>
        <div className="team-modal-body">
          <p className="kicker kicker-plain">{member.role}</p>
          <h3 id="team-modal-name">{member.name}</h3>
          <p className="team-modal-overview">{member.overview}</p>
          <ul className="team-modal-tags">
            {member.focus.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>,
    document.body
  );
}

function useWideViewport(minWidth) {
  const [wide, setWide] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia(`(min-width: ${minWidth}px)`).matches
  ));

  useEffect(() => {
    const media = window.matchMedia(`(min-width: ${minWidth}px)`);
    const onChange = () => setWide(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [minWidth]);

  return wide;
}

function Home() {
  const heroRef = useRef(null);
  const showHeroAlt = useWideViewport(641);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return undefined;
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!finePointer) return undefined;

    const onMove = (event) => {
      const box = hero.getBoundingClientRect();
      hero.style.setProperty('--mx', `${event.clientX - box.left}px`);
      hero.style.setProperty('--my', `${event.clientY - box.top}px`);
    };

    hero.addEventListener('pointermove', onMove, { passive: true });
    return () => hero.removeEventListener('pointermove', onMove);
  }, []);

  return (
    <div className="home">
      <div className="home-fold">
        <section className="hero" ref={heroRef}>
          <div className="hero-glow" />
          <div className="container hero-grid">
          <div className="hero-copy">
            <p className="kicker kicker-plain kicker kicker-plain-plain">INSTATENDERS · PROCUREMENT PLATFORM</p>
            <h1>
            Smarter procurement. <em>Stronger supply chains.</em>
            </h1>
            <p className="hero-lead">
            We bring procurement, suppliers, and supply-chain processes together on a single digital platform. InstaTenders enables organizations to discover tenders, manage sourcing and RFQs, collaborate with suppliers, and make faster, data-driven procurement decisions.
            </p>
            <div className="hero-actions">
              <Link to="/contact" className="btn btn-green">Start a conversation</Link>
              <Link to="/services" className="btn btn-outline">See how we deliver</Link>
            </div>
            <div className="hero-metrics">
              <div>
                <strong>End-to-End Procurement</strong>
                <span>From purchase requests to purchase orders</span>
              </div>
              <div>
                <strong>Supplier Network</strong>
                <span>Connect buyers with trusted suppliers</span>
              </div>
              <div>
                <strong>Digital & Transparent</strong>
                <span>Real-time visibility across procurement activities</span>
              </div>
            </div>
          </div>

          <div className="hero-stage">
            <div className="hero-panel">
              <span className="panel-dot" />
              <span>Live process view</span>
              <h3>PR → PO in one flow</h3>
              <div className="panel-bars">
                <i />
                <i />
                <i />
                <i />
              </div>
              <p>Role-based approvals, spend visibility, and a clean audit trail on every buy.</p>
            </div>
            <img
              className="hero-photo hero-photo-main"
              src="/Assets/Images/home-hero-team.jpg"
              alt="Team reviewing operations"
              width="1100"
              height="722"
              fetchPriority="high"
              decoding="async"
            />
            {showHeroAlt && (
              <img
                className="hero-photo hero-photo-alt"
                src="/Assets/Images/home-hero-analytics.jpg"
                alt="Spend and process analytics"
                width="900"
                height="600"
                loading="lazy"
                decoding="async"
              />
            )}
          </div>
        </div>
      </section>
      </div>

      <section className="customer-logos" aria-label="Our customers">
        <div className="container">
          <p className="kicker kicker-plain">Our customers</p>
          <div className="customer-logos-viewport">
            <div className="customer-logos-track">
              {[0, 1].map((copy) => (
                <ul className="customer-logos-set" key={copy} aria-hidden={copy === 1}>
                  {customers.map((item) => (
                    <li
                      className={`customer-logos-item${item.wide ? ' is-wide' : ''}${item.large ? ' is-large' : ''}`}
                      key={`${copy}-${item.file}`}
                    >
                      <img
                        src={customerSrc(item.file)}
                        alt={copy === 0 ? item.name : ''}
                        title={item.name}
                        width={item.wide ? 200 : item.large ? 176 : 148}
                        height={item.large ? 86 : 64}
                        decoding="async"
                      />
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section offerings" id="applications">
        <div className="container">
          <Reveal>
            <p className="kicker kicker-plain">Platform + practice</p>
            <h2 className="section-title">Four ways we work with you</h2>
            <p className="section-lead">
            Software when you need a system. A desk when you need capacity. Consulting when the operating model itself has to change.
            </p>
          </Reveal>
          <div className="bento">
            {offerings.map((item, index) => (
              <Reveal key={item.title} delay={index * 90} className={item.className}>
                <Link to={item.to} className="bento-card">
                  <span>{item.short}</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                 
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section story-section" id="digitalization">
        <div className="container story-grid">
          <Reveal className="story-copy">
            <span className="story-index" aria-hidden="true">01</span>
            <p className="kicker kicker-plain"></p>
            <h2 className="section-title">
              A focus on <em>digitalization</em> to improve your supply chain processes
            </h2>
            <p className="section-lead">
              We keep end-to-end digitization in the foreground to optimize workflows and bring transparency to the supply chain processes. Our strategic procurement solutions give you the lens to magnify the areas of excess spending, giving you a unique opportunity for savings. Access our end-to-end procurement solutions to uplift your business with optimum transactional efficiency and tangible benefits.
            </p>
            <ul className="story-points">
              <li>
                <span>01</span>
                Microservice based process management tools.
              </li>
              <li>
                <span>02</span>
                Easy to configure and customize.
              </li>
              <li>
                <span>03</span>
                Role based access and rights.
              </li>
            </ul>
          </Reveal>
          <Reveal delay={140} className="story-media">
            <div className="story-orbit" aria-hidden="true" />
            <figure className="story-frame">
              <img
                src={`${process.env.PUBLIC_URL}/Assets/Images/vision.jpg`}
                alt="Digital procurement workspace with live process visibility"
                width="900"
                height="675"
                loading="lazy"
                decoding="async"
              />
            </figure>
           
          </Reveal>
        </div>
      </section>

      <section className="section story-section story-section-alt" id="process-management">
        <div className="container story-grid story-grid-flip">
          <Reveal className="story-copy">
            <span className="story-index" aria-hidden="true">02</span>
            <p className="kicker kicker-plain"></p>
            <h2 className="section-title">
              Strategic process management for better <em>operational outcomes</em>
            </h2>
            <p className="section-lead">
              We offer a wide range of procurement services to strategically and efficiently manage your organization’s direct and indirect procurement demands. Our team prioritizes process optimization at all phases by harnessing digitization and focusing on automation. Platform-driven procurement process brings transparent procurement process and efficiency.
            </p>
            <ul className="story-tiles">
              <li>
                <strong>IT + domain</strong>
                Domain expertise solutions with the blend of IT and expertise.
              </li>
              <li>
                <strong>Customer-centric</strong>
                Customer centric operating models.
              </li>
              <li>
                <strong>TCO</strong>
                Total cost of ownership.
              </li>
            </ul>
          </Reveal>
          <Reveal delay={140} className="story-media story-media-dark">
            <figure className="story-frame">
              <img
                src={`${process.env.PUBLIC_URL}/Assets/Images/mission.jpg`}
                alt="Process automation and operational excellence in procurement"
                width="900"
                height="675"
                loading="lazy"
                decoding="async"
              />
            </figure>
            
          </Reveal>
        </div>
      </section>

      <section className="section process-section">
        <div className="container">
          <Reveal>
            <p className="kicker kicker-plain">How engagement works</p>
            <h2 className="section-title">Four beats, then a steady run</h2>
          </Reveal>
          <div className="process-line">
            {steps.map((step, index) => (
              <Reveal key={step.n} delay={index * 80} className="process-step">
                <span>{step.n}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section why-section">
        <div className="container why-layout">
          <Reveal>
            <p className="kicker kicker-plain">Why InstaTenders</p>
            <h2 className="section-title">Built for teams who need the work to move</h2>
            <p className="section-lead">
              Sustainable procurement, resilient supply chains, and process excellence.
            </p>
          </Reveal>
          <div className="why-list">
            {reasons.map((item, index) => (
              <Reveal key={item.title} delay={index * 60} className="why-row">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* <section className="section team-section" id="team">
        <div className="container">
          <Reveal>
            <p className="kicker kicker-plain">Our people</p>
            <h2 className="section-title">The team behind the desk</h2>
            <p className="section-lead">
              Leadership and operators who stay with the process — from buying and stores to accounts and partnerships.
            </p>
          </Reveal>
          <div className="team-grid">
            {members.map((member, index) => (
              <Reveal key={`${member.name}-${member.role}`} delay={index * 70} className="team-card-wrap">
                <article className={`team-card${profile?.name === member.name ? ' is-open' : ''}`}>
                  <figure className="team-photo">
                    <img src={member.src} alt={member.name} width="400" height="400" loading="lazy" decoding="async" />
                    <button
                      type="button"
                      className="team-photo-hint"
                      onClick={() => setProfile(member)}
                      aria-label={`View profile of ${member.name}`}
                      aria-haspopup="dialog"
                      aria-expanded={profile?.name === member.name}
                    >
                      View profile
                    </button>
                  </figure>
                  <div className="team-copy">
                    <h3>{member.name}</h3>
                    <p>{member.role}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          {profile ? <TeamProfile member={profile} onClose={() => setProfile(null)} /> : null}
        </div>
      </section> */}

      <section className="cta-band">
        <Reveal className="container cta-inner">
          <div>
            <p className="kicker kicker-plain">Next step</p>
            <h2>Tell us the process that is slowing you down</h2>
            <p>A specialist will help you choose the right mix of software, desk, and advisory.</p>
          </div>
          <Link to="/contact" className="btn btn-green">Book a working session</Link>
        </Reveal>
      </section>
    </div>
  );
}

export default Home;
